import * as React from 'react'
import { Button, Tooltip, Icon, message } from 'antd'
import { Modal } from 'antd-v4'
import { Link } from 'react-router'
import { inject } from 'mobx-react'
import { API } from '@getgreenline/homi-shared'
import { AuditAPI, AuditModels } from '@getgreenline/audits'
import { AuditStore } from '../EditAuditStore'
import { Routes } from '../../../../../../constants/Routes'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { parseErrorMsg } from '../../../../../../utilities/helpers'
import { BLAZE_COMPANY } from '../../../../../../constants/general'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  auditStore: AuditStore
  audit: AuditModels.CreateInventoryAudit
}

interface State {
  isModalVisible: boolean
  isRefreshing: boolean
}

@inject('currentCompanyStore')
export class RefreshSnapshotButton extends React.Component<Props, State> {
  MESSAGE_KEY = 'refreshMessageKey'

  company = this.props.currentCompanyStore!.company!

  constructor(props: Props) {
    super(props)

    this.state = {
      isModalVisible: false,
      isRefreshing: false,
    }
  }

  onClickRefreshButton = async () => {
    if (this.props.currentCompanyStore!.company!.auditCompleteMode === 'replace') {
      return this.refreshQuantities()
    }

    if (this.state.isModalVisible) {
      await this.refreshQuantities()
      return this.setState({ isModalVisible: false })
    }
    return this.setState({ isModalVisible: true })
  }

  refreshQuantities = async () => {
    const { companyId, id: auditId } = this.props.audit

    this.setState({ isRefreshing: true })

    try {
      message.loading({
        content: 'Updating expected quantities',
        key: this.MESSAGE_KEY,
      })

      await AuditAPI.refreshAuditQuantities(companyId, auditId)

      const auditResponse = await API.getInventoryAuditById(companyId, auditId)

      this.props.audit.populate(auditResponse)

      message.success({
        content: 'Expected quantities updated',
        key: this.MESSAGE_KEY,
      })
    } catch (error) {
      message.error({
        content: parseErrorMsg(error) || 'There was an error refreshing expected quantities',
        key: this.MESSAGE_KEY,
      })
    } finally {
      this.setState({ isRefreshing: false })
    }
  }

  render() {
    const { isModalVisible, isRefreshing } = this.state

    return (
      <>
        <Button
          icon='redo'
          className='d-flex align-items-center mr-2'
          onClick={this.onClickRefreshButton}
        >
          Refresh expected quantities
          <div className='ml-1'>
            <Tooltip
              title={
                <span>
                  <span>
                    {BLAZE_COMPANY} takes a snapshot of your inventory when you start an audit, so
                    all expected quantities are from that time. To update your expected quantities,
                    you can refresh but be careful if you are using{' '}
                  </span>
                  <Link to={Routes.DASHBOARD_SETTINGS_INVENTORY(this.company.id)}>relative</Link>
                  <span> mode.</span>
                </span>
              }
            >
              <Icon type='info-circle' />
            </Tooltip>
          </div>
        </Button>

        <Modal
          title='Update expected quantities'
          okText='Confirm'
          visible={isModalVisible}
          confirmLoading={isRefreshing}
          okButtonProps={{
            disabled: isRefreshing,
          }}
          onCancel={() => this.setState({ isModalVisible: false })}
          onOk={this.onClickRefreshButton}
        >
          <div className='d-flex mb-3' style={{ alignItems: 'center' }}>
            <Icon type='exclamation-circle' theme='twoTone' style={{ fontSize: 50 }} />

            <div className='pl-4'>
              <div>
                This will refresh <b>expected</b> quantities.
              </div>
              <div>Your audits are set to apply relative +/- discrepancies.</div>
              <div>
                If there have been any inventory changes including sales/refunds during this audit,
                you will have to count them again.
              </div>
            </div>
          </div>
        </Modal>
      </>
    )
  }
}
